<template>
  <div
    v-if="banners && banners.length > 0"
    class="banner-slider"
    :class="containerClass"
  >
    <transition-group
      name="banner-slide"
      mode="out-in"
      tag="div"
      class="banner-container"
    >
      <div
        v-for="(banner, index) in banners"
        v-show="index === currentIndex"
        :key="banner.id"
        class="banner-item"
        :class="{ clickable: banner.modelNumber || banner.url }"
        @click="handleBannerClick(banner)"
      >
        <img
          :src="getBannerUrl(banner.filename)"
          :alt="alt"
          @load="handleImageLoad(banner)"
          @error="handleImageError(banner)"
        >
      </div>
    </transition-group>

    <!-- Slider indicators -->
    <div
      v-if="banners && banners.length > 1"
      class="slider-indicators"
    >
      <button
        v-for="(banner, index) in banners"
        :key="`indicator-${banner.id}`"
        class="indicator"
        :class="{ active: index === currentIndex }"
        :aria-label="`Go to slide ${index + 1}`"
        @click="goToSlide(index)"
      />
    </div>

    <!-- Navigation arrows -->
    <template v-if="banners && banners.length > 1">
      <button
        class="nav-arrow nav-prev"
        aria-label="Previous slide"
        @click="previousSlide"
      >
        <svg viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"
          />
        </svg>
      </button>
      <button
        class="nav-arrow nav-next"
        aria-label="Next slide"
        @click="nextSlide"
      >
        <svg viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"
          />
        </svg>
      </button>
    </template>
  </div>
</template>

<script>
export default {
  name: 'BannerSlider',
  props: {
    location: {
      type: String,
      required: true,
      validator: value => ['menu', 'order'].includes(value)
    },
    alt: {
      type: String,
      default: 'Banner'
    },
    containerClass: {
      type: String,
      default: ''
    },
    autoPlayInterval: {
      type: Number,
      default: 5000 // 5 seconds
    }
  },
  data () {
    return {
      banners: [],
      currentIndex: 0,
      autoPlayTimer: null,
      loading: true
    }
  },
  computed: {
    apiBaseUrl () {
      // Check if we're in production environment
      if (window.location.hostname.includes('maluwilz.de')) {
        return 'https://www.maluwilz.de'
      }
      return ''
    }
  },
  mounted () {
    this.loadBanners()
  },
  beforeDestroy () {
    this.stopAutoPlay()
  },
  methods: {
    async loadBanners () {
      try {
        const response = await fetch(`${this.apiBaseUrl}/imgw/banners/location/${this.location}`)
        if (response.ok) {
          const data = await response.json()
          // Ensure we always have an array
          this.banners = Array.isArray(data) ? data : []
          if (this.banners.length > 1) {
            this.startAutoPlay()
          }
        } else {
          // If response is not ok, ensure banners is an array
          this.banners = []
        }
      } catch (error) {
        console.error('Error loading banners:', error)
        // On error, ensure banners is an array
        this.banners = []
      } finally {
        this.loading = false
      }
    },

    getBannerUrl (filename) {
      return `${this.apiBaseUrl}/imgw/${filename}`
    },

    handleBannerClick (banner) {
      if (banner.url) {
        // Open URL in new tab
        window.open(banner.url, '_blank', 'noopener,noreferrer')
      } else if (banner.modelNumber) {
        // Emit event for model number handling
        this.$emit('banner-click', {
          modelNumber: banner.modelNumber,
          filename: banner.filename
        })
      }
    },

    handleImageLoad (banner) {
      // Image loaded successfully
    },

    handleImageError (banner) {
      console.error(`Failed to load banner: ${banner.filename}`)
      // Remove failed banner from the list
      const index = this.banners.findIndex(b => b.id === banner.id)
      if (index > -1) {
        this.banners.splice(index, 1)
        if (this.currentIndex >= this.banners.length) {
          this.currentIndex = 0
        }
      }
    },

    nextSlide () {
      if (!this.banners || this.banners.length === 0) return
      this.currentIndex = (this.currentIndex + 1) % this.banners.length
      this.resetAutoPlay()
    },

    previousSlide () {
      if (!this.banners || this.banners.length === 0) return
      this.currentIndex = this.currentIndex === 0 ? this.banners.length - 1 : this.currentIndex - 1
      this.resetAutoPlay()
    },

    goToSlide (index) {
      this.currentIndex = index
      this.resetAutoPlay()
    },

    startAutoPlay () {
      if (this.autoPlayTimer) return
      this.autoPlayTimer = setInterval(() => {
        this.nextSlide()
      }, this.autoPlayInterval)
    },

    stopAutoPlay () {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer)
        this.autoPlayTimer = null
      }
    },

    resetAutoPlay () {
      this.stopAutoPlay()
      this.startAutoPlay()
    }
  }
}
</script>

<style scoped>
.banner-slider {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #f5f5f5;
}

.banner-container {
  position: relative;
  width: 100%;
  padding-bottom: 37.55%; /* Maintain aspect ratio 1673:628 */
}

.banner-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.banner-item.clickable {
  cursor: pointer;
}

.banner-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f5f5f5;
}

/* Slider Indicators */
.slider-indicators {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s;
  padding: 0;
}

.indicator.active {
  background: #fff;
  width: 30px;
  border-radius: 5px;
}

.indicator:hover {
  background: #fff;
}

/* Navigation Arrows */
.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  z-index: 10;
  padding: 0;
}

.nav-arrow:hover {
  background: rgba(0, 0, 0, 0.7);
}

.nav-arrow svg {
  width: 24px;
  height: 24px;
}

.nav-prev {
  left: 15px;
}

.nav-next {
  right: 15px;
}

/* Slide Transitions */
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: opacity 0.5s ease;
}

.banner-slide-enter,
.banner-slide-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .nav-arrow {
    width: 35px;
    height: 35px;
  }

  .nav-prev {
    left: 10px;
  }

  .nav-next {
    right: 10px;
  }

  .slider-indicators {
    bottom: 10px;
  }

  .indicator {
    width: 8px;
    height: 8px;
  }

  .indicator.active {
    width: 24px;
  }
}

/* Hide navigation on very small screens */
@media (max-width: 480px) {
  .nav-arrow {
    display: none;
  }
}
</style>
